/**
 * Tree drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const treeDrawingsPack: ToilePack = {
    slug: 'tree-drawings',
    category: 'illustration',
    name: 'Tree drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'oak', label: 'Old oak', subject: 'an old oak with a broad gnarled trunk and spreading branches, its roots gripping the ground'},
        {key: 'willow', label: 'Weeping willow', subject: 'a weeping willow leaning over a pond, its long branches touching the water'},
        {key: 'palm', label: 'Palm trees', subject: 'two palm trees leaning over a beach, their fronds lifted by the wind and their shadows on the sand'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a pine', 'autumn', 'a baobab'],
    directionHint: 'try: a pine',
    seo: {
        title: 'Tree Drawings: Old Oak, Weeping Willow, Palm Trees',
        metaDescription: 'Nine tree drawings: a broad old oak, a weeping willow over a pond and palm trees on a beach, each drawn in pencil, pen and ink, or charcoal.',
        h1: 'Tree drawings: an old oak, a weeping willow, palm trees',
        tagline: 'Three trees, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three trees: a broad old oak with a gnarled trunk and roots gripping the ground, a weeping willow with branches touching a pond, and two palms leaning over a beach. Each is drawn in pencil, pen and ink, and charcoal, so the bark, the branching and the crown shape can be compared in three media. Type a different tree or a twist, such as a pine or autumn, and the same media carry it.',
        useCases: [
            'a single-tree study for a sketchbook',
            'a framed print for a study or hallway',
            'a bark and branching practice sheet',
            'a seaside or garden wall print',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three trees: an old oak, a weeping willow over a pond, and two palms on a beach. Each tree appears in three media.'},
            {q: 'Can I ask for a different tree?', a: 'Yes. Type a tree or twist, such as a pine, autumn or a baobab. A typed direction is a request, and the drawing may differ from the samples.'},
            {q: 'How do the three styles differ?', a: 'Pencil builds layered shading from sharp detail to deep darks. Pen and ink uses contour lines and crosshatching for bark. Charcoal uses smudged masses with crisp accents.'},
            {q: 'Is each drawing wide or tall?', a: 'Tall. The tree drawings are taller than they are wide, which suits a crown rising against the sky.'},
        ],
    },
}
